<!--
	App legend: one section per `layerConfig.ts` module, one checkable row per published layer.
	`legend.grid` layers render as a column × row checkbox matrix; `legend.partOf` layers ride on
	their primary's checkbox. Symbology disclosures come from the style JSON (`symbology.ts`), so
	the legend can't drift from the map.
-->
<script lang="ts">
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import LegendRow from '#lib/components/LegendRow.svelte';
	import CollapseToggleButton from '#lib/components/CollapseToggleButton.svelte';
	import LegendSection from '#lib/components/LegendSection.svelte';
	import { legendStore, type MapLayer } from '#lib/stores/legendStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import type { ILayerSource } from '#lib/types.js';
	// Safe value import: `layerConfig.ts` doesn't import this component.
	import { moduleTable } from '../layerConfig';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import {
		deriveSymbology,
		patchStyle,
		type SymbologyItem
	} from '#lib/components/map/legend/symbology.js';
	import SymbologySwatch from '#lib/components/map/legend/SymbologySwatch.svelte';
	import { RLGSP_SPRITE_PATH } from '../loadMapSprites';

	// Wide view of the envelope, so table keys can index it.
	const systemData = $derived(
		geodataStore.systemData as Record<string, Record<string, ILayerSource | null>> | null
	);

	interface Row {
		layerId: string;
		grid?: { column: string; row: string };
		partOf?: string;
		source: ILayerSource | null;
		legend: MapLayer | null;
		symbology: SymbologyItem[];
	}

	type GridCell = Row & { grid: NonNullable<Row['grid']> };

	// Group a module's grid-declared layers into a column × row cell matrix,
	// labels in table order. Null when the module declares no grid layers.
	// The grid's symbology comes from its first cell — grid layers share one
	// root feature and palette, differing only by feature filter.
	function buildGrid(rows: Row[]) {
		const cells = rows.filter((r): r is GridCell => !!r.grid);
		if (!cells.length) return null;
		const columns = [...new Set(cells.map((c) => c.grid.column))];
		const rowLabels = [...new Set(cells.map((c) => c.grid.row))];
		return {
			columns,
			available: cells.some((c) => c.source && c.legend),
			symbology: cells[0].symbology,
			rows: rowLabels.map((label) => ({
				label,
				cells: columns.map(
					(column) => cells.find((c) => c.grid.column === column && c.grid.row === label) ?? null
				)
			}))
		};
	}

	// One section per module: a cell grid when configured, plus one row per
	// remaining layer. A layer only earns a row when `buildSystemLayers` emitted
	// a source for it and `parseLegendLayers` installed its legend entry.
	const sections = $derived(
		Object.entries(moduleTable)
			.map(([moduleId, m]) => {
				const rows: Row[] = Object.entries(m.layers).map(([layerId, entry]) => {
					const source = systemData?.[moduleId]?.[layerId] ?? null;
					return {
						layerId,
						grid: entry.legend?.grid,
						partOf: entry.legend?.partOf,
						source,
						legend: legendStore.layers.find((l) => l.systemId === layerId) ?? null,
						symbology: entry.legend?.hideSymbology
							? []
							: deriveSymbology(entry.map.style, entry.legend?.symbology)
					};
				});
				return {
					moduleId,
					label: m.label,
					grid: buildGrid(rows),
					// One row per primary layer; `partOf` sublayers get no row of their
					// own — the primary's checkbox toggles them (`linked`) and their
					// symbology items are appended to the primary's unless the sublayer
					// sets `hideSymbology`.
					listRows: rows
						.filter((r) => !r.grid && !r.partOf)
						// A layer the area doesn't publish drops out entirely, rather than
						// claiming its data is unavailable.
						.flatMap(({ source, legend, ...r }) => {
							if (!source || !legend) return [];
							const subRows = rows.filter((o) => o.partOf === r.layerId);
							return {
								...r,
								source,
								legend,
								symbology: [...r.symbology, ...subRows.flatMap((o) => o.symbology)],
								linked: subRows.map((o) => o.legend).filter((l): l is MapLayer => !!l)
							};
						})
				};
			})
			// A module whose every layer dropped out shows no heading either.
			.filter((s) => s.grid?.available || s.listRows.length)
	);

	// Symbology disclosure state, keyed by layer id (or `grid:<moduleId>`).
	let expanded = $state<Record<string, boolean>>({});

	// Toggle a legend layer plus any `partOf` sublayers riding on its checkbox.
	function toggleLayer(legend: MapLayer, linked: MapLayer[] = []) {
		const enabled = !legend.layerAllEnabled;
		for (const l of [legend, ...linked]) {
			legendStore.setAllLegendLayerVisibility({ layerId: l.layerId, enabled });
		}
	}
</script>

{#snippet symbologyList(items: SymbologyItem[])}
	<ul class="symbologyList">
		{#each items as item, i (i)}
			<li>
				<SymbologySwatch swatch={item.swatch} spritePath={RLGSP_SPRITE_PATH} />
				<span>{item.label}</span>
			</li>
		{/each}
	</ul>
{/snippet}

<div class="legendBox">
	{#each sections as section (section.moduleId)}
		<LegendSection heading={section.label}>
			{#if section.grid}
				{#if section.grid.available}
					<table class="legendGrid">
						<thead>
							<tr>
								<td></td>
								{#each section.grid.columns as column (column)}
									<th scope="col">{column}</th>
								{/each}
							</tr>
						</thead>
						<tbody>
							{#each section.grid.rows as gridRow (gridRow.label)}
								<tr>
									<th scope="row">{gridRow.label}</th>
									{#each gridRow.cells as cell, i (section.grid.columns[i])}
										<td>
											{#if cell?.legend && cell?.source}
												{@const legend = cell.legend}
												<label class="cellTarget">
													<input
														type="checkbox"
														aria-label="{section.grid.columns[i]} - {gridRow.label}"
														checked={legend.layerAllEnabled}
														onchange={() => toggleLayer(legend)} />
												</label>
											{/if}
										</td>
									{/each}
								</tr>
							{/each}
						</tbody>
					</table>
					{#if section.grid.symbology.length}
						{@const gridKey = `grid:${section.moduleId}`}
						<div class="symbologyToggle">
							<span>Symbology</span>
							<CollapseToggleButton
								aria-label="Toggle grid symbology"
								collapsed={!expanded[gridKey]}
								oncollapsetoggle={() => (expanded[gridKey] = !expanded[gridKey])} />
						</div>
						{#if expanded[gridKey]}
							{@render symbologyList(section.grid.symbology)}
						{/if}
					{/if}
				{/if}
			{/if}
			{#each section.listRows as row (row.layerId)}
				<div class="legendRowBox">
					<div class="rowLine">
						<LegendRow
							label={row.layerId}
							checked={row.legend.layerAllEnabled}
							onchange={() => toggleLayer(row.legend, row.linked)} />
						{#if row.symbology.length}
							<CollapseToggleButton
								aria-label="Toggle {row.layerId} symbology"
								collapsed={!expanded[row.layerId]}
								oncollapsetoggle={() => (expanded[row.layerId] = !expanded[row.layerId])} />
						{:else}
							<!-- Single-symbol layer: its one patch sits at the row's right edge. -->
							<span class="patchSlot">
								<LegendPatch
									color={row.legend.legendItems[0]?.color ?? PATCH_FALLBACK}
									shape={patchStyle(row.source.layer.type)} />
							</span>
						{/if}
					</div>
					{#if row.symbology.length && expanded[row.layerId]}
						{@render symbologyList(row.symbology)}
					{/if}
				</div>
			{/each}
		</LegendSection>
	{/each}
</div>

<style>
	.legendBox {
		width: calc(var(--legend-width) + 9rem);
	}

	.rowLine {
		display: flex;
		justify-content: space-between;
		align-items: center;

		:global(> label) {
			flex: 1;
		}

		/* Same width as the sibling rows' collapse toggles, so the patch lines up with their chevrons. */
		.patchSlot {
			display: flex;
			justify-content: center;
			width: var(--height-button-sm);
		}
	}

	.symbologyToggle {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: var(--space-2);
	}

	.symbologyList {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		margin: var(--space-1) 0 var(--space-2);
		padding: 0 0 0 var(--space-6);
		list-style: none;

		li {
			display: flex;
			align-items: center;
			gap: var(--space-2);
		}
	}

	.legendGrid {
		margin-bottom: var(--space-3);
		border-collapse: collapse;
		width: 100%;

		thead th {
			vertical-align: bottom;
			padding: 0 var(--space-1) var(--space-1);
			font-weight: var(--weight-semibold);
			font-size: var(--font-size-xs);
			text-align: center;
		}

		tbody th {
			font-weight: var(--weight-regular);
			text-align: left;
		}

		td {
			padding: 0;
		}

		/* The label fills its cell so the whole cell is the click target. */
		.cellTarget {
			display: flex;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			min-height: var(--target-min);
		}
	}
</style>
